import { useEffect, useState } from "react";
import {
  subscribeToBasicPollVotes,
  subscribeToEmbeddedBasicPolls,
} from "../../../lib/data/basicPolls";
import { hasSubmittedVoteForPoll } from "../../../lib/basic-polls/vote-submission";

function voterIdsMatch(left, right) {
  if (!Array.isArray(left) || !Array.isArray(right)) return false;
  if (left.length !== right.length) return false;
  return left.every((id, index) => id === right[index]);
}

export function useSchedulerEditorEmbeddedPolls({ isEditing, schedulerId }) {
  const [embeddedPolls, setEmbeddedPolls] = useState([]);
  const [embeddedPollsLoading, setEmbeddedPollsLoading] = useState(false);
  const [embeddedPollVoteCounts, setEmbeddedPollVoteCounts] = useState({});
  // Per-poll authoritative vote metadata for the edit-after-votes structural
  // lock: `{ count, voterIds, known }`. `known` is false until the vote listener
  // for a poll RESOLVES at least once, so an edit opened during the initial load
  // window is treated as unknown (fail-closed — the lock stays on). On listener
  // error we leave `known` unset (do NOT fabricate a 0 count); the card display
  // still falls back to 0 via `|| 0`, but the structural lock treats the poll as
  // unknown. `embeddedPollVoteCounts` (plain counts) is preserved for the card.
  const [embeddedPollVoteMeta, setEmbeddedPollVoteMeta] = useState({});

  useEffect(() => {
    if (!isEditing || !schedulerId) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: synchronous state init/reset for this subscription effect
      setEmbeddedPolls([]);
      setEmbeddedPollsLoading(false);
      return;
    }
    setEmbeddedPollsLoading(true);
    const unsubscribe = subscribeToEmbeddedBasicPolls(
      schedulerId,
      (polls) => {
        setEmbeddedPolls(polls || []);
        setEmbeddedPollsLoading(false);
      },
      () => {
        setEmbeddedPolls([]);
        setEmbeddedPollsLoading(false);
      }
    );
    return () => unsubscribe();
  }, [isEditing, schedulerId]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: synchronous state init/reset for this subscription effect
    setEmbeddedPollVoteCounts({});
    setEmbeddedPollVoteMeta({});
    if (!isEditing || !schedulerId || embeddedPolls.length === 0) return () => {};

    const unsubscribers = embeddedPolls.map((poll) =>
      subscribeToBasicPollVotes(
        "scheduler",
        schedulerId,
        poll.id,
        (voteDocs) => {
          const submitted = (voteDocs || []).filter((voteDoc) =>
            hasSubmittedVoteForPoll(poll, voteDoc)
          );
          const count = submitted.length;
          const voterIds = submitted
            .map((voteDoc) => voteDoc.id)
            .filter(Boolean);
          setEmbeddedPollVoteCounts((previous) => {
            if (previous[poll.id] === count) return previous;
            return { ...previous, [poll.id]: count };
          });
          setEmbeddedPollVoteMeta((previous) => {
            const current = previous[poll.id];
            if (
              current &&
              current.known === true &&
              current.count === count &&
              voterIdsMatch(current.voterIds, voterIds)
            ) {
              return previous;
            }
            return { ...previous, [poll.id]: { count, voterIds, known: true } };
          });
        },
        () => {
          // Fail-closed for the structural lock: do NOT write a fake known-0
          // entry here (that would read as "no votes" and unlock structure).
          // Leave meta unset so `known` stays false. The display count falls
          // back to 0 via `|| 0` at the card.
          setEmbeddedPollVoteCounts((previous) =>
            Object.prototype.hasOwnProperty.call(previous, poll.id)
              ? previous
              : { ...previous, [poll.id]: 0 }
          );
        }
      )
    );

    return () => {
      unsubscribers.forEach((unsubscribe) => {
        if (typeof unsubscribe === "function") unsubscribe();
      });
    };
  }, [embeddedPolls, isEditing, schedulerId]);

  return {
    embeddedPolls,
    setEmbeddedPolls,
    embeddedPollsLoading,
    embeddedPollVoteCounts,
    embeddedPollVoteMeta,
  };
}
